import { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { ColorPicker, Tooltip } from 'antd';

import { OBJECT_COLOR_PALETTE } from '../constants';

const COLOR_PRESETS = [{ label: 'Presets', colors: OBJECT_COLOR_PALETTE }];

// Geometry only. The color is applied inline instead: interpolating it here
// would mint a new styled-components class per distinct value, and a hue drag
// produces one per frame.
const swatchStyles = css`
	width: 12px;
	height: 12px;
	border-radius: 2px;
	flex-shrink: 0;
`;

const StaticSwatch = styled.span`
	display: inline-block;
	${swatchStyles}
`;

// Reads as the same 12px chip it replaced — the interactivity is in the cursor,
// the hover ring and the focus ring, not in extra chrome that would crowd a
// 28px tree row.
const SwatchButton = styled.button`
	display: inline-block;
	padding: 0;
	border: none;
	cursor: pointer;
	${swatchStyles}

	&:hover {
		box-shadow: 0 0 0 2px rgba(128, 128, 128, 0.35);
	}

	&:focus-visible {
		outline: 2px solid rgba(128, 128, 128, 0.8);
		outline-offset: 2px;
	}
`;

/**
 * antd's picker emits an AggregationColor; `style.color` and `style.opacity` are
 * stored separately. These two conversions came from `ColorPickerField`, which
 * this component replaces.
 */

// AggregationColor -> { hex, opacity }
const fromAggregationColor = aggregationColor => {
	const { r, g, b, a } = aggregationColor.toRgb();
	// toHexString() folds alpha into the string, so derive hex from RGB directly
	const hex = '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
	return { hex, opacity: Math.round(a * 100) };
};

// { hex, opacity } -> a value string the picker displays with the right alpha.
// The hex is sliced because a stored color may already carry its own alpha pair.
const toPickerValue = (hex, opacity) => {
	const alpha = Math.round(((opacity ?? 100) / 100) * 255)
		.toString(16)
		.padStart(2, '0');
	return `${hex.slice(0, 7)}${alpha}`;
};

/**
 * Per-object color picker — the swatch in a Detection Results row *is* the
 * trigger, so color is edited exactly where it is displayed, the way the
 * detection pointer next to it already works.
 *
 * Three tiers, because the map is the expensive one. `onChange` moves the draft
 * only — the panel is controlled by `value`, so it has to. `onChangeComplete`
 * previews on the map: antd withholds it until a panel drag ends, so a hue drag
 * previews once on release rather than once per frame. The single save happens
 * when the popover closes, and only if the value actually moved — commit-on-pick,
 * the way the detection pointer works, would be a PATCH per drag.
 *
 * **Escape cancels.** antd's `onOpenChange` doesn't say *why* the popover closed
 * and its Popover doesn't handle Escape at all, so the key is caught on the
 * document during the capture phase and recorded before the close is handled.
 *
 * With no `onCommit` the swatch renders as a plain chip — a node that isn't an
 * editable object still shows its color.
 *
 * @param {object} props
 * @param {string} props.objectKey
 * @param {string} props.label - object display name, for accessible labels
 * @param {string} props.color - the saved color, always hex (see `toHexColor`)
 * @param {number} [props.opacity] - the saved opacity, 0–100
 * @param {(objectKey: string, hex: string, opacity: number) => void} [props.onPreview]
 *   - called when a change settles, not while a slider is being dragged
 * @param {(objectKey: string, hex: string, opacity: number, onSettled: () => void) => void} [props.onCommit]
 * @param {() => void} [props.onCancel] - drop the preview, saving nothing
 */
const ObjectColorPicker = ({
	objectKey,
	label,
	color,
	opacity = 100,
	onPreview,
	onCommit,
	onCancel,
}) => {
	const [open, setOpen] = useState(false);
	// The in-progress edit; null means "untouched since the popover opened".
	const [draft, setDraft] = useState(null);
	// Mirrors `draft` so closing can read the latest edit without making the
	// close handler — and the document listener that depends on it — churn on
	// every frame of a hue drag.
	const draftRef = useRef(null);
	// Escape closes the popover itself, so antd reports that same close a second
	// time. One open, one outcome — and because the Escape listener is on the
	// document in the capture phase, cancelling is always the outcome that wins.
	//
	// It gates the change handlers for the same reason: closing doesn't unmount
	// the panel, so a drag in progress keeps its document listeners and its
	// mouseup would preview a color the cancel just dropped.
	const finishedRef = useRef(false);
	// Read through a ref so the unmount effect below depends on `open` alone: a
	// changing `onCancel` identity would re-run it and cancel a live edit.
	const onCancelRef = useRef(onCancel);
	onCancelRef.current = onCancel;

	const finish = useCallback(
		cancelled => {
			if (finishedRef.current) return;
			finishedRef.current = true;
			setOpen(false);
			const edit = draftRef.current;
			draftRef.current = null;

			// Nothing to save: cancelled, never touched, or dragged back to where
			// it started. All three drop the draft and clear the preview.
			const unchanged = edit && edit.hex === color && edit.opacity === opacity;
			if (cancelled || !edit || unchanged) {
				setDraft(null);
				return onCancel?.();
			}

			// The draft deliberately outlives the close. `color` only becomes the
			// committed value when the optimistic cache write lands, a tick from
			// now, so clearing it here would show the saved color in between — the
			// swatch would flicker new -> old -> new while the map stayed new.
			//
			// The save clears it instead: by then the prop has caught up, or the
			// save failed and rolled back and the saved color is what belongs on
			// the row. Identity-checked so a newer draft isn't dropped by an older
			// save reporting back.
			onCommit(objectKey, edit.hex, edit.opacity, () =>
				setDraft(current => (current === edit ? null : current))
			);
		},
		[color, opacity, objectKey, onCommit, onCancel]
	);

	// An open picker whose object drops out of `data.objects` — a detections
	// refetch, or the collection losing its detections — unmounts without ever
	// closing, and the preview override lives on `CollectionResults`, which stays
	// mounted. Left alone the map would show an unsaved color indefinitely.
	useEffect(() => {
		if (!open) return undefined;
		return () => {
			if (!finishedRef.current) onCancelRef.current?.();
		};
	}, [open]);

	useEffect(() => {
		if (!open) return;
		const handleKeyDown = event => {
			if (event.key === 'Escape') finish(true);
		};
		document.addEventListener('keydown', handleKeyDown, true);
		return () => document.removeEventListener('keydown', handleKeyDown, true);
	}, [open, finish]);

	const handleOpenChange = useCallback(
		next => {
			if (next) {
				finishedRef.current = false;
				draftRef.current = null;
				setDraft(null);
				setOpen(true);
				return;
			}
			finish(false);
		},
		[finish]
	);

	// Records the edit for the swatch and the panel, which both render `shown`.
	const applyDraft = useCallback(value => {
		const next = fromAggregationColor(value);
		draftRef.current = next;
		setDraft(next);
		return next;
	}, []);

	const handleChange = useCallback(
		value => {
			if (finishedRef.current) return;
			applyDraft(value);
		},
		[applyDraft]
	);

	// Only the panel's drag controls wait for mouseup. A preset click, a hex
	// keystroke or an arrow key on the hue slider isn't a drag, so antd reports
	// those here immediately.
	const handleChangeComplete = useCallback(
		value => {
			if (finishedRef.current) return;
			const next = applyDraft(value);
			onPreview?.(objectKey, next.hex, next.opacity);
		},
		[applyDraft, objectKey, onPreview]
	);

	const shown = draft ?? { hex: color, opacity };

	// After the hooks, never before them: the hook order has to be the same on
	// every render regardless of which variant this node renders as.
	if (!onCommit) return <StaticSwatch style={{ backgroundColor: color }} />;

	return (
		<ColorPicker
			value={toPickerValue(shown.hex, shown.opacity)}
			onChange={handleChange}
			onChangeComplete={handleChangeComplete}
			open={open}
			onOpenChange={handleOpenChange}
			presets={COLOR_PRESETS}
			placement="bottomLeft"
			size="small">
			<Tooltip title={`Color for ${label}`}>
				<SwatchButton
					type="button"
					style={{ backgroundColor: shown.hex }}
					aria-label={`Color for ${label}`}
				/>
			</Tooltip>
		</ColorPicker>
	);
};

export default ObjectColorPicker;
